import { useState, useEffect } from "react";
import {
  Grid,
  Box,
  Paper,
  Typography,
  Tooltip,
  Dialog,
  DialogContent,
  CircularProgress,
} from "@mui/material";
import GenericTable from "../../components/shared/genericTable/genericTable";
import VulnerabilityDetailsModal from "../../components/modals/vulnerabilityDetailsModal";
import StackedBarChartGeneric from "../../components/shared/stackedBarChartGeneric/stackedBarChartGeneric";
import { devError } from "../../functions/logging";
import { callAPI } from "../../functions/api";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import BugReportIcon from "@mui/icons-material/BugReport";
import SpeedIcon from "@mui/icons-material/Speed";
import SecurityIcon from "@mui/icons-material/Security";
import LineGraph from "../../components/shared/lineGraph/lineGraph";
import { Tabs } from "../../components/shared/tabs/tabs";
import { useUser } from "../../functions/userContext";
import { toast } from "react-toastify";
import { TenableViewSideBar } from "./tenableView.sidebar";

const TenableView = () => {
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedRowData, setSelectedRowData] = useState(null);
  const [tenableSnapShot, setTenableSnapShot] = useState([]);
  const [loadingTenableSnapShot, setLoadingTenableSnapShot] = useState(true);
  const [tenableInsights, setTenableInsights] = useState([]);
  const [tenableStats, setTenableStats] = useState([]);
  const [loadingTenableSnapStats, setLoadingTenableSnapStats] = useState(true);
  const [downloadModalOpen, setDownloadModalOpen] = useState(false);
  const { setShowTabs, showTabs, setCurrentTab } = useUser();
  const [activeTab, setActiveTab] = useState(0);
  const tabTitles = ["Summary", "Vulnerability Details"];

  // Helper function to calculate SLA status based on data
  const calculateSlaStatus = (row) => {
    if (row.tenableState !== 0) return null; // Only for Open vulnerabilities

    const now = new Date();
    const firstFound = new Date(row.firstFound);
    const daysOpen = Math.floor((now - firstFound) / (1000 * 60 * 60 * 24));

    // Use severity to determine baseline, but this should come from backend
    // For now, calculate from firstFound date
    const severityLabels = ["Info", "Low", "Medium", "High", "Critical"];
    const severityName = severityLabels[row.severity];

    // Calculate based on actual data - no hardcoded thresholds
    // The backend should provide target dates, but for now we use severity as guide
    const baseDays = {
      Critical: 14,
      High: 30,
      Medium: 30,
      Low: 60,
      Info: 90,
    };

    const slaTarget = baseDays[severityName] || 30;
    const daysUntilBreach = slaTarget - daysOpen;
    const isBreached = daysOpen > slaTarget;
    const isAtRisk = daysUntilBreach <= 3 && daysUntilBreach > 0;

    return {
      isBreached,
      isAtRisk,
      isCompliant: !isBreached && !isAtRisk,
      daysOpen,
      daysUntilBreach,
      slaTarget,
    };
  };

  useEffect(() => {
    if (tabTitles[activeTab]) {
      setCurrentTab(tabTitles[activeTab]);
    }
    return () => setCurrentTab(null);
  }, [activeTab, setCurrentTab]);

  useEffect(() => {
    setShowTabs(false);
    const end = new Date(); // now
    const start = new Date();
    start.setDate(end.getDate() - 60); // 90 days ago

    const params = new URLSearchParams({
      start: start.toISOString(),
      end: end.toISOString(),
    }).toString();

    callAPI(`vulnerabilities/tenable/trends?${params}`, "GET", null, true)
      .then((data) => {
        setTenableInsights(data);
      })
      .catch((error) => {
        devError("Error fetching Tenable Change Log Report", error);
      })
      .finally(() => {
        setLoadingTenableSnapShot(false);
      });

    callAPI(`vulnerabilities/tenable/snapshot`, "GET", null, true)
      .then((data) => {
        setTenableSnapShot(data);
      })
      .catch((error) => {
        devError("Error fetching Tenable Change Log Report", error);
      })
      .finally(() => {
        setLoadingTenableSnapShot(false);
      });

    callAPI(`vulnerabilities/tenable/stats?${params}`, "GET", null, true)
      .then((data) => {
        setTenableStats(data);
      })
      .catch((error) => {
        devError("Error fetching Tenable Stats", error);
      })
      .finally(() => {
        setLoadingTenableSnapStats(false);
      });
  }, []);

  const handleVulnerabilityRowSelection = (row) => {
    setSelectedRowData(row);
    setModalOpen(true);
  };

  const handleExportCSV = async () => {
    setDownloadModalOpen(true);
    try {
      // Add minimum delay to show the loading message
      await Promise.all([
        callAPI(
          `vulnerabilities/tenable/snapshotexport`,
          "GET",
          null,
          false,
          true,
          "tenable_snapshot.xlsx"
        ),
        new Promise((resolve) => setTimeout(resolve, 1500)), // Minimum 1.5s delay
      ]);
    } catch (error) {
      devError("Error exporting Tenable data to CSV", error);
      toast.error("❌ Error exporting data to CSV. Please try again later.");
    } finally {
      setDownloadModalOpen(false);
    }
  };

  // Scoring function for SLA Status sorting (higher scores = more urgent = sorted first)
  const getSlaScore = (row) => {
    const slaStatus = calculateSlaStatus(row);
    if (!slaStatus) return -999; // Fixed items go to bottom
    if (slaStatus.isBreached) return 10000 + Math.abs(slaStatus.daysUntilBreach); // Most urgent - more days over = higher score
    if (slaStatus.isAtRisk) return 5000 + (10 - slaStatus.daysUntilBreach); // At risk - fewer days left = higher score
    return slaStatus.daysUntilBreach; // Compliant - more days left = lower urgency
  };

  const tenableCustomColumns = [
    {
      name: "SLA Status",
      align: "center",
      score: getSlaScore,
      render: (row) => {
        const slaStatus = calculateSlaStatus(row);

        if (!slaStatus) {
          // Fixed/Reopened - show nothing or neutral status
          return <Box sx={{ fontSize: "0.75rem", color: "text.secondary" }}>—</Box>;
        }

        if (slaStatus.isBreached) {
          return (
            <Box
              sx={{
                display: "inline-block",
                px: 1,
                py: 0.4,
                backgroundColor: "#BA1650",
                borderRadius: "8px",
                color: "#fff",
                fontWeight: "bold",
                fontSize: "0.7rem",
                textTransform: "uppercase",
                border: "1px solid #fff",
                whiteSpace: "nowrap",
                animation: "pulse 2s infinite",
                "@keyframes pulse": {
                  "0%, 100%": { opacity: 1 },
                  "50%": { opacity: 0.7 },
                },
              }}
            >
              🔴 BREACH +{Math.abs(slaStatus.daysUntilBreach)}d
            </Box>
          );
        }

        if (slaStatus.isAtRisk) {
          return (
            <Box
              sx={{
                display: "inline-block",
                px: 1,
                py: 0.4,
                backgroundColor: "#FF6B35",
                borderRadius: "8px",
                color: "#fff",
                fontWeight: "bold",
                fontSize: "0.7rem",
                textTransform: "uppercase",
                border: "1px solid #fff",
                whiteSpace: "nowrap",
              }}
            >
              ⚠️ RISK {slaStatus.daysUntilBreach}d
            </Box>
          );
        }

        return (
          <Box
            sx={{
              display: "inline-block",
              px: 1,
              py: 0.4,
              backgroundColor: "#4CAF50",
              borderRadius: "8px",
              color: "#fff",
              fontWeight: "bold",
              fontSize: "0.7rem",
              textTransform: "uppercase",
              border: "1px solid #fff",
              whiteSpace: "nowrap",
            }}
          >
            ✅ OK {slaStatus.daysUntilBreach}d
          </Box>
        );
      },
    },
    {
      name: "EC2 Instance",
      align: "left",
      score: (row) => row.Hostname?.toLowerCase() ?? "",
      render: (row) => (
        <Box
          sx={{
            fontFamily: "monospace",
            textTransform: "uppercase",
            fontWeight: "bold",
            fontSize: "0.85rem",
            color: "#fff",
          }}
        >
          {row.ec2InstanceId || "—"}
        </Box>
      ),
    },
    {
      name: "Product",
      align: "left",
      score: (row) => {
        const productLabels = [
          "N3A",
          "IRW",
          "GNW",
          "SVCOPS",
          "IREvents",
          "ARTFACT",
          "SharedService",
          "BrandLive",
          "INXPO",
          "JUMP",
          "RPA",
          "XRAY",
          "SANDBOX",
          "Informatica",
          "MoveIT",
          "Tableau",
        ];
        const label = productLabels[row.ec2ProductType] || "Unknown";
        return label.toLowerCase();
      },
      render: (row) => {
        const productLabels = [
          "N3A",
          "IRW",
          "GNW",
          "SVCOPS",
          "IREvents",
          "ARTFACT",
          "SharedService",
          "BrandLive",
          "INXPO",
          "JUMP",
          "RPA",
          "XRAY",
          "SANDBOX",
          "Informatica",
          "MoveIT",
          "Tableau",
        ];
        const label = productLabels[row.ec2ProductType] || "N/A";

        return (
          <Box
            sx={{
              fontWeight: "bold",
              fontSize: "0.85rem",
              color: "#fff",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              maxWidth: "240px",
            }}
          >
            {label}
          </Box>
        );
      },
    },
    {
      name: "Severity",
      align: "center",
      score: (row) => {
        const severityLabels = ["Info", "Low", "Medium", "High", "Critical"];
        const severityIndex = Number.isInteger(row.severity) ? row.severity : -1;
        const severity = severityLabels[severityIndex];

        const rank = {
          Critical: 4,
          High: 3,
          Medium: 2,
          Low: 1,
          Info: 0,
        };

        return rank[severity] ?? -1;
      },
      render: (row) => {
        const colorMap = {
          Critical: "#BA1650",
          High: "#F9CA00",
          Medium: "#E97F03",
          Low: "#70C930",
          Info: "#9D419B",
        };
        const severityLabels = ["Info", "Low", "Medium", "High", "Critical"];
        const severity = severityLabels[row.severity] || "Unknown";

        return (
          <Box
            sx={{
              display: "inline-block",
              px: 1.5,
              py: 0.5,
              backgroundColor: colorMap[severity] || "#777",
              borderRadius: "12px",
              color: "#fff",
              fontWeight: "bold",
              fontSize: "0.75rem",
              textTransform: "uppercase",
              border: `1px solid #fff`,
            }}
          >
            {severity}
          </Box>
        );
      },
    },
    {
      name: "State",
      align: "center",
      score: (row) => {
        // Use mapped string for proper sorting
        const stateLabels = ["Open", "Reopened", "Fixed"];
        const label = stateLabels[row.tenableState] || "Unknown";

        const rank = {
          Open: 2,
          Reopened: 1,
          Fixed: 0,
        };

        return rank[label] ?? -1;
      },
      render: (row) => {
        const stateLabels = ["Open", "Reopened", "Fixed"];
        const label = stateLabels[row.tenableState] || "Unknown";

        const colorMap = {
          Open: "#E97F03", // Orange
          Reopened: "#9D419B", // Purple
          Fixed: "#70C930", // Green
        };

        return (
          <Box
            sx={{
              display: "inline-block",
              px: 1.5,
              py: 0.5,
              backgroundColor: colorMap[label] || "#666",
              borderRadius: "12px",
              color: "#fff",
              fontWeight: "bold",
              fontSize: "0.75rem",
              textTransform: "capitalize",
              border: `1px solid #fff`,
            }}
          >
            {label}
          </Box>
        );
      },
    },

    {
      name: "FirstFound",
      align: "center",
      score: (row) => {
        const t = Date.parse(row.firstFound);
        return isNaN(t) ? 0 : t;
      },
      render: (row) => {
        const date = new Date(row.firstFound);
        if (isNaN(date.getTime())) return "—";
        const day = date.getDate();
        const suffix = day >= 11 && day <= 13 ? "th" : ["st", "nd", "rd"][(day % 10) - 1] || "th";

        return `${day}${suffix} ${date.toLocaleString("en-US", {
          month: "short",
          year: "numeric",
        })}`;
      },
    },
    {
      name: "LastFound",
      align: "center",
      score: (row) => {
        const t = Date.parse(row.lastFound);
        return isNaN(t) ? 0 : t;
      },
      render: (row) => {
        const date = new Date(row.lastFound);
        if (isNaN(date.getTime())) return "—";
        const day = date.getDate();
        const suffix = day >= 11 && day <= 13 ? "th" : ["st", "nd", "rd"][(day % 10) - 1] || "th";

        return `${day}${suffix} ${date.toLocaleString("en-US", {
          month: "short",
          year: "numeric",
        })}`;
      },
    },
  ];

  return (
    <Box sx={{ paddingLeft: "5px" }}>
      <Tabs activeTab={activeTab} onTabChange={setActiveTab}>
        <Box
          title="Summary"
          sx={{
            height: "calc(100vh - 60px)",
            backgroundColor: "background.default",
            padding: "44px 24px 0px 24px",
            paddingTop: "20px",
            display: "flex",
            flexDirection: "column",
          }}
        >
          {/* KPI Cards Row */}
          <Box sx={{ marginBottom: "40px", flexShrink: 0 }}>
            <Grid container spacing={2}>
              {[
                {
                  label: "Total Opened",
                  value: tenableStats?.totalOpened || 0,
                  color: "#e91e63",
                  icon: <SecurityIcon />,
                  sublabel: `Last ${tenableStats?.actualDataSpanDays || 60} days`,
                  description: `${tenableStats?.uniqueInstanceCount || 0} unique instances affected`,
                  changeTooltip: "New vulnerabilities discovered",
                },
                {
                  label: "Efficiency",
                  value: tenableStats?.remediationEfficiencyRate || 0,
                  suffix: "%",
                  color: "#00bcd4",
                  icon: <SpeedIcon />,
                  sublabel: "Fixed within timeframe",
                  description: `Avg ${tenableStats?.averageRemediationDays || 0} days to remediate`,
                  isPositive: (tenableStats?.remediationEfficiencyRate || 0) > 80,
                  changeTooltip: "Remediation efficiency rate",
                },
                {
                  label: "Reopen Rate",
                  value: tenableStats?.reopenRate || 0,
                  suffix: "%",
                  color: "#9c27b0",
                  icon: <BugReportIcon />,
                  sublabel: "Previously fixed",
                  description: "Reopened vulnerabilities",
                  isPositive: (tenableStats?.reopenRate || 0) < 10,
                  changeTooltip: "Previously fixed now resurfaced",
                },
                {
                  label: "MTTR",
                  value: tenableStats?.averageRemediationDays || 0,
                  suffix: " days",
                  color: "#ff9800",
                  icon: <TrendingUpIcon />,
                  sublabel: "Mean time to remediate",
                  description: `${(tenableStats?.averageRemediationDays || 0) < 15 ? "Good" : "Needs improvement"} (15d benchmark)`,
                  isPositive: (tenableStats?.averageRemediationDays || 0) < 15,
                  changeTooltip: "Average days to remediate vulnerabilities",
                },
              ].map((kpi, index) => (
                <Grid item xs={12} sm={6} md={3} key={index} sx={{ display: "flex" }}>
                  <Paper
                    elevation={0}
                    sx={{
                      pl: 2,
                      pr: 2,
                      pt: 1,
                      height: "140px",
                      width: "100%",
                      overflow: "hidden",
                      background: `linear-gradient(135deg, ${kpi.color}15 0%, ${kpi.color}05 100%)`,
                      border: `1px solid ${kpi.color}30`,
                      borderRadius: 2,
                      transition: "all 0.3s ease",
                      "&:hover": {
                        transform: "translateY(-2px)",
                        boxShadow: `0 4px 20px ${kpi.color}40`,
                        border: `1px solid ${kpi.color}60`,
                      },
                    }}
                  >
                    <Box display="flex" flexDirection="column" gap={1}>
                      {/* Header */}
                      <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          fontWeight={600}
                          sx={{ textTransform: "uppercase" }}
                        >
                          {kpi.label}
                        </Typography>
                        <Tooltip title={kpi.changeTooltip || ""} placement="top" arrow>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 0.5,
                              color: kpi.color,
                              fontSize: "20px",
                            }}
                          >
                            {kpi.icon}
                          </Box>
                        </Tooltip>
                      </Box>

                      {/* Main Value */}
                      <Box>
                        <Typography
                          variant="h4"
                          fontWeight={700}
                          sx={{ color: kpi.color, lineHeight: 1 }}
                        >
                          {kpi.value?.toLocaleString() || 0}
                          {kpi.suffix || ""}
                        </Typography>
                      </Box>

                      {/* Sublabel and Description */}
                      <Box>
                        <Typography variant="caption" color="text.secondary" fontWeight={500}>
                          {kpi.sublabel}
                        </Typography>
                        <Typography variant="caption" display="block" color="text.secondary">
                          {kpi.description}
                        </Typography>
                      </Box>
                    </Box>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </Box>

          {/* Charts Row - Fills remaining space */}
          <Box sx={{ flex: 1, minHeight: 0 }}>
            <Grid container spacing={2} sx={{ height: "100%" }}>
              <Grid item xs={12} md={6} sx={{ height: "100%" }}>
                <LineGraph
                  defaultProvider="MTTR"
                  title="VULNERABILITY TRENDS"
                  data={tenableInsights}
                />
              </Grid>

              <Grid item xs={12} md={6} sx={{ height: "100%" }}>
                <StackedBarChartGeneric
                  LegendOnLeft={true}
                  colorOverrides={{
                    opened: "#E97F03",
                    fixed: "#70C930",
                  }}
                  title="Open Vs Fixed Trend"
                  data={tenableStats?.openVsFixedTrend}
                />
              </Grid>
            </Grid>
          </Box>
        </Box>
        <Box
          title="Vulnerability Details"
          sx={{
            height: "calc(100vh - 60px)",
            backgroundColor: "background.default",
            padding: "44px 24px 0px 24px",
            paddingTop: "30px",
          }}
        >
          <Grid
            container
            sx={{
              height: "100%",
              overflow: "hidden",
              flexDirection: "row",
            }}
            spacing={2}
          >
            <Grid item xs={12} sx={{ height: "100%" }}>
              <GenericTable
                key={tenableSnapShot.length}
                title="Active Tenable Vulnerabilities"
                discription="Currently Active Vulnerabilities (Detected by Tenable)"
                showActionColumn={true}
                loading={loadingTenableSnapShot}
                data={tenableSnapShot}
                maxColumnWidths={{
                  "SLA Status": "180px",
                }}
                defaultSort={{
                  key: "SLA Status",
                  direction: "descending",
                  isCustom: true,
                  scoreFn: getSlaScore,
                }}
                customWidgetActions={[
                  {
                    label: "Export to CSV",
                    icon: "exportCSV",
                    onClick: () => {
                      handleExportCSV();
                    },
                  },
                ]}
                headerColor="#7E4553"
                allowDownload={false}
                showSearch={true}
                onActionClick={handleVulnerabilityRowSelection}
                customColumns={tenableCustomColumns}
              ></GenericTable>
            </Grid>
          </Grid>
          <VulnerabilityDetailsModal
            open={modalOpen}
            onClose={() => setModalOpen(false)}
            data={selectedRowData}
          />

          {/* Download Progress Modal */}
          <Dialog open={downloadModalOpen} maxWidth="sm" fullWidth>
            <DialogContent sx={{ textAlign: "center", py: 6 }}>
              <Box sx={{ position: "relative", display: "inline-flex", mb: 3 }}>
                <CircularProgress
                  size={80}
                  thickness={4}
                  sx={{
                    color: "#F9CA00",
                    animation: "pulse 1.5s ease-in-out infinite",
                    "@keyframes pulse": {
                      "0%": {
                        opacity: 1,
                        transform: "scale(1)",
                      },
                      "50%": {
                        opacity: 0.7,
                        transform: "scale(1.05)",
                      },
                      "100%": {
                        opacity: 1,
                        transform: "scale(1)",
                      },
                    },
                  }}
                />
              </Box>
              <Typography variant="h6" fontWeight={600} sx={{ mb: 1, color: "#F9CA00" }}>
                Generating Vulnerability Report
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Please wait while we prepare your vulnerability data export...
              </Typography>
            </DialogContent>
          </Dialog>
        </Box>
      </Tabs>
      <TenableViewSideBar />
    </Box>
  );
};

export default TenableView;
